import { BooleanInput } from '@angular/cdk/coercion';
import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { UserService } from 'app/core/user/user.service';
import { ADMIN_ROLE, LoginMembership, SUPER_ADMIN_ROLE, User } from 'app/core/user/user.types';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'user',
    templateUrl: './user.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'user',
    imports: [
        MatButtonModule,
        MatMenuModule,
        MatIconModule,
        NgClass,
        MatDividerModule,
    ],
})
export class UserComponent implements OnInit, OnDestroy {
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean = true;
    user: User;
    isSwitchingAccount = false;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _userService: UserService,
        private _authService: AuthService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the user status
     *
     * @param status
     */
    updateUserStatus(status: string): void {
        // Return if user is not available
        if (!this.user) {
            return;
        }

        // Update the user
        this._userService
            .update({
                ...this.user,
                status,
            })
            .subscribe();
    }

    /**
     * Sign out
     */
    signOut(): void {
        this._router.navigate(['/sign-out']);
    }

    get switchableMemberships(): LoginMembership[] {
        // This portal only has Admin/SuperAdmin routes. Keep the switcher to
        // contexts that can actually open this application.
        return (this.user?.memberships ?? []).filter(
            (membership) =>
                membership.isActive &&
                (membership.roleName === ADMIN_ROLE || membership.roleName === SUPER_ADMIN_ROLE)
        );
    }

    membershipLabel(membership: LoginMembership): string {
        if (membership.flatNumber) {
            return membership.flatNumber;
        }
        return membership.roleName?.toLowerCase() === 'admin'
            ? 'Society administration'
            : membership.roleName || 'Society access';
    }

    switchAccount(membership: LoginMembership): void {
        if (this.isSwitchingAccount || membership.membershipId === this.user?.membershipId) {
            return;
        }

        this.isSwitchingAccount = true;
        this._authService.selectMembership(membership.membershipId).subscribe({
            next: () => {
                this.isSwitchingAccount = false;
                this._router.navigateByUrl(
                    this.user.roleName === SUPER_ADMIN_ROLE
                        ? '/platform/societies'
                        : '/dashboard'
                );
            },
            error: () => {
                this.isSwitchingAccount = false;
                this._changeDetectorRef.markForCheck();
            },
        });
    }
}
